import type {
	UploadFailureReason,
	UploadProgressMessage,
	UploadProgressMessageV2,
	UploadProgressPhoto,
	UploadProgressSource,
	UploadProgressStage,
} from '@sosocio/creator-types/app';

export type UploadProgressIdentity = {
	batchId: string | null;
	sequence: number;
	stage?: UploadProgressStage;
	submittedCount: number;
	supersededBatchIdentities?: readonly string[];
	uploadSessionId: string | null;
};

export const EXTERNAL_UPLOAD_RETRY_BASE_MS = 1_000;
export const EXTERNAL_UPLOAD_RETRY_MAX_MS = 30_000;

/**
 * Calculates capped exponential retry delay with up to 25% positive jitter.
 *
 * @param attempt Zero-based consecutive retry attempt.
 * @param random Random value in the inclusive range `[0, 1]`.
 * @param baseDelayMs Initial delay before the first retry.
 * @param maxDelayMs Maximum delay after backoff and jitter.
 * @returns Delay in milliseconds.
 */
export function getExternalUploadRetryDelay(
	attempt: number,
	random = Math.random(),
	baseDelayMs = EXTERNAL_UPLOAD_RETRY_BASE_MS,
	maxDelayMs = EXTERNAL_UPLOAD_RETRY_MAX_MS,
): number {
	const safeAttempt = Math.max(
		0,
		Math.floor(attempt),
	);
	const exponentialDelay = Math.min(
		maxDelayMs,
		baseDelayMs * (2 ** safeAttempt),
	);
	const jitter = exponentialDelay * 0.25 * Math.min(
		1,
		Math.max(
			0,
			random,
		),
	);

	return Math.min(
		maxDelayMs,
		Math.round(exponentialDelay + jitter),
	);
}

const uploadFailureReasons: ReadonlySet<UploadFailureReason> = new Set([
	'network',
	'processing',
	'save',
	'unknown',
	'validation',
]);

const uploadProgressSources: ReadonlySet<UploadProgressSource> = new Set([
	'external-device',
	'native-picker',
]);

const uploadProgressStages: ReadonlySet<UploadProgressStage> = new Set([
	'aborted',
	'completed',
	'failed',
	'processing',
	'saving',
	'selected',
	'transferring',
]);

/**
 * Checks that a terminal payload identifies every submitted photo exactly once.
 *
 * @param value Unknown terminal photo list.
 * @param submittedCount Number of photos selected for the batch.
 * @returns Whether the list is complete and contains no duplicate identifiers.
 */
function isCompleteProgressPhotoArray(
	value: unknown,
	submittedCount: number,
): value is UploadProgressPhoto[] {
	return (
		isProgressPhotoArray(value)
		&& value.length === submittedCount
		&& new Set(value.map((photo) => photo.id)).size === submittedCount
	);
}

/**
 * Checks whether a value is a finite, non-negative integer used by progress counts.
 *
 * @param value Unknown runtime value.
 * @returns Whether the value is a valid count or sequence number.
 */
function isNonNegativeInteger(value: unknown): value is number {
	return (
		typeof value === 'number'
		&& Number.isInteger(value)
		&& value >= 0
	);
}

/**
 * Validates the optional photo list included in an AppSync progress message.
 *
 * @param value Unknown runtime value.
 * @returns Whether every entry has a supported identifier and optional URL.
 */
function isProgressPhotoArray(value: unknown): value is UploadProgressPhoto[] {
	return (
		Array.isArray(value)
		&& value.every((photo) => (
			isRecord(photo)
			&& (
				typeof photo.id === 'number'
				|| typeof photo.id === 'string'
			)
			&& (
				typeof photo.url === 'undefined'
				|| typeof photo.url === 'string'
			)
		))
	);
}

/**
 * Narrows an unknown JSON value to an object with string keys.
 *
 * @param value Unknown runtime value.
 * @returns Whether the value is a non-null, non-array object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
	return (
		typeof value === 'object'
		&& value !== null
		&& !Array.isArray(value)
	);
}

/**
 * Validates a protocol-v2 progress source.
 *
 * @param value Unknown runtime value.
 * @returns Whether the value is a supported progress source.
 */
function isUploadProgressSource(value: unknown): value is UploadProgressSource {
	return (
		typeof value === 'string'
		&& uploadProgressSources.has(value as UploadProgressSource)
	);
}

/**
 * Validates a protocol-v2 upload stage.
 *
 * @param value Unknown runtime value.
 * @returns Whether the value is a supported upload stage.
 */
function isUploadProgressStage(value: unknown): value is UploadProgressStage {
	return (
		typeof value === 'string'
		&& uploadProgressStages.has(value as UploadProgressStage)
	);
}

/**
 * Requires an identifier used to construct an external-upload channel.
 *
 * @param value Candidate product or user identifier.
 * @param label Identifier label used in the validation error.
 * @returns The supplied non-null identifier.
 */
function requireExternalUploadId(
	value: string | number | null | undefined,
	label: string,
): string | number {
	if (value === null || value === undefined) {
		throw new Error(`Missing ${label} id for external upload channel`);
	}

	return value;
}

/**
 * Builds the AppSync channel shared by the external uploader and its consumer.
 *
 * @param productId Product whose upload session owns the channel.
 * @param userId User authorized to publish or consume the channel.
 * @returns Stable external-upload channel slug.
 */
export function buildExternalUploadChannel(
	productId: string | number,
	userId: string | number,
): string {
	const safeProductId = requireExternalUploadId(
		productId,
		'product',
	);
	const safeUserId = requireExternalUploadId(
		userId,
		'user',
	);

	return `product/${safeProductId}/external_upload/${safeUserId}`;
}

/**
 * Builds the stable key used to remember a superseded protocol-v2 batch.
 *
 * @param uploadSessionId Publisher upload-session identity.
 * @param batchId Batch identity inside the publisher session.
 * @returns Collision-safe serialized identity.
 */
export function getUploadProgressIdentityKey(
	uploadSessionId: string,
	batchId: string,
): string {
	return JSON.stringify([
		uploadSessionId,
		batchId,
	]);
}

/**
 * Narrows a progress message to the correlated protocol-v2 shape.
 *
 * @param message Validated progress message.
 * @returns Whether the message carries protocol-v2 correlation fields.
 */
export function isUploadProgressMessageV2(
	message: UploadProgressMessage,
): message is UploadProgressMessageV2 {
	return message.version === 2;
}

/**
 * Validates an untrusted JSON value before it crosses the AppSync boundary.
 *
 * @param value Parsed AppSync payload.
 * @returns A typed legacy or protocol-v2 message, or `null` when validation fails.
 */
export function parseUploadProgressMessage(value: unknown): UploadProgressMessage | null {
	if (!isRecord(value)) {
		return null;
	}

	if (
		!isNonNegativeInteger(value.submittedCount)
		|| (
			typeof value.photos !== 'undefined'
			&& !isProgressPhotoArray(value.photos)
		)
		|| (
			typeof value.progressPercentage !== 'undefined'
			&& (
				typeof value.progressPercentage !== 'number'
				|| !Number.isFinite(value.progressPercentage)
				|| value.progressPercentage < 0
				|| value.progressPercentage > 100
			)
		)
	) {
		return null;
	}

	if (
		typeof value.version === 'undefined'
		|| value.version === 1
	) {
		if (typeof value.progressPercentage !== 'number') {
			return null;
		}

		return {
			photos: value.photos,
			progressPercentage: value.progressPercentage,
			submittedCount: value.submittedCount,
			version: value.version,
		};
	}

	if (
		value.version !== 2
		|| typeof value.batchId !== 'string'
		|| value.batchId.length === 0
		|| !isNonNegativeInteger(value.processedCount)
		|| typeof value.sentAt !== 'string'
		|| !Number.isFinite(Date.parse(value.sentAt))
		|| !isNonNegativeInteger(value.sequence)
		|| !isUploadProgressSource(value.source)
		|| !isUploadProgressStage(value.stage)
		|| !isNonNegativeInteger(value.transferredCount)
		|| typeof value.uploadSessionId !== 'string'
		|| value.uploadSessionId.length === 0
		|| value.processedCount > value.submittedCount
		|| value.processedCount > value.transferredCount
		|| value.transferredCount > value.submittedCount
		|| (
			value.stage === 'completed'
			&& (
				value.submittedCount === 0
				|| value.processedCount !== value.submittedCount
				|| value.transferredCount !== value.submittedCount
				|| !isCompleteProgressPhotoArray(
					value.photos,
					value.submittedCount,
				)
			)
		)
		|| (
			typeof value.failureReason !== 'undefined'
			&& (
				typeof value.failureReason !== 'string'
				|| !uploadFailureReasons.has(value.failureReason as UploadFailureReason)
			)
		)
	) {
		return null;
	}

	return {
		batchId: value.batchId,
		failureReason: value.failureReason as UploadFailureReason | undefined,
		photos: value.photos,
		processedCount: value.processedCount,
		...(typeof value.progressPercentage === 'number'
			? {
				progressPercentage: value.progressPercentage,
			}
			: {}),
		sentAt: value.sentAt,
		sequence: value.sequence,
		source: value.source,
		stage: value.stage,
		submittedCount: value.submittedCount,
		transferredCount: value.transferredCount,
		uploadSessionId: value.uploadSessionId,
		version: 2,
	};
}

/**
 * Rejects legacy ticks after v2 begins and stale sequences within one correlated batch.
 *
 * @param current Correlation fields stored by the active external actor.
 * @param incoming Validated progress message being considered.
 * @returns Whether the incoming message may advance or replace the active batch.
 */
export function shouldAcceptUploadProgress(
	current: UploadProgressIdentity,
	incoming: UploadProgressMessage,
): boolean {
	if (!isUploadProgressMessageV2(incoming)) {
		return current.uploadSessionId === null;
	}

	const incomingIdentity = getUploadProgressIdentityKey(
		incoming.uploadSessionId,
		incoming.batchId,
	);

	if (current.supersededBatchIdentities?.includes(incomingIdentity)) {
		return false;
	}

	if (
		current.uploadSessionId !== incoming.uploadSessionId
		|| current.batchId !== incoming.batchId
	) {
		return true;
	}
	if (
		current.stage === 'completed'
		|| current.stage === 'failed'
		|| current.stage === 'aborted'
	) {
		return false;
	}

	return (
		incoming.submittedCount === current.submittedCount
		&& incoming.sequence > current.sequence
	);
}
